import type { ChainId } from '@morpho-org/blue-sdk'

import { parseUnits } from 'viem'

import { ChainConfig } from '@/config'
import {
  filterAssetSelectorsByChainIds,
  filterValidAssetSelectors
} from '@/helpers/filter/assetSelectors'
import { orpc } from '@/lib/orpc/client'
import { type MarketListInput, marketListInputSchema } from '@/lib/procedures/marketList.contract'

import { type MarketOrderBy, MARKETS_PAGE_SIZE, type MarketsFilters } from './config'

export type MarketListQueryInput = Omit<MarketListInput, 'cursor'>

const MAX_SEARCH_LENGTH = 100
const MAX_CURATOR_ID_LENGTH = 100

const finite = (value: number | null) =>
  value != null && Number.isFinite(value) ? value : undefined
const usdBound = (value: number | null) => {
  const usd = finite(value)
  return usd != null ? Math.max(0, usd) : undefined
}
const apyBound = (percent: number | null) => {
  const value = finite(percent)
  return value != null ? value / 100 : undefined
}
/** A percent LLTV bound as a WAD string (86 → 0.86e18). */
const lltvBound = (percent: number | null) => {
  const value = finite(percent)
  if (value == null) return undefined
  return parseUnits(Math.min(100, Math.max(0, value)).toFixed(4), 16).toString()
}

/** Unserved `?chains=` ids are dropped; none left means every available chain. */
export function resolveMarketsChainIds(chains: number[]): ChainId[] {
  const available = chains.filter(ChainConfig.isAvailable)
  return available.length > 0 ? available : ChainConfig.AVAILABLE_CHAIN_IDS
}

/** Valid `chainId:address` selectors, pruned to the queried chains. */
const toSelectors = (selectors: string[], chainIds: ChainId[]) =>
  // oxlint-disable-next-line typescript/consistent-type-assertions -- validated by the selector regex
  filterAssetSelectorsByChainIds(filterValidAssetSelectors(selectors), chainIds) as
    | `${number}:0x${string}`[]
    | undefined

/** The `market.list` input for the table's URL filters and sort. */
export function toMarketListInput(
  filters: MarketsFilters,
  { orderBy, desc }: { orderBy: MarketOrderBy; desc: boolean }
): MarketListQueryInput {
  const chainIds = resolveMarketsChainIds(filters.chains)
  const curatorIds = [
    ...new Set(filters.curators.filter(id => id && id.length <= MAX_CURATOR_ID_LENGTH))
  ]
  return {
    limit: MARKETS_PAGE_SIZE,
    orderBy,
    orderDirection: desc ? 'desc' : 'asc',
    search: filters.search.slice(0, MAX_SEARCH_LENGTH) || undefined,
    chainIds,
    loanAssets: toSelectors(filters.loanAssetIdsFilter, chainIds),
    collateralAssets: toSelectors(filters.collateralAssetIdsFilter, chainIds),
    curatorIds: curatorIds.length ? curatorIds : undefined,
    sizeUsdGte: usdBound(filters.sizeUsd_gte),
    sizeUsdLte: usdBound(filters.sizeUsd_lte),
    totalLiquidityUsdGte: usdBound(filters.totalLiquidityUsd_gte),
    totalLiquidityUsdLte: usdBound(filters.totalLiquidityUsd_lte),
    avgNetBorrowApyGte: apyBound(filters.avgNetBorrowApy_gte),
    avgNetBorrowApyLte: apyBound(filters.avgNetBorrowApy_lte),
    lltvGte: lltvBound(filters.lltv_gte),
    lltvLte: lltvBound(filters.lltv_lte),
    includeFacets: true,
    countTotal: true
  }
}

/** Whether the input fits the contract's bounds; only a selection over the list-size limit fails. */
export const isMarketListInputWithinBounds = (input: MarketListQueryInput) =>
  marketListInputSchema.safeParse(input).success

/** Shared by the server prefetch and the client query, so the hydrated cache entry matches. */
export const marketListInfiniteOptions = (input: MarketListQueryInput) =>
  orpc.market.list.infiniteOptions({
    input: (cursor: string | undefined) => ({ ...input, cursor }),
    initialPageParam: undefined,
    getNextPageParam: page => page.nextCursor ?? undefined,
    staleTime: 60 * 1000
  })
